import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { ManagementAiPresetControllerService, ManagementAiPresetRequest, ManagementAiPresetResponse } from 'api-ai-hub';
import { SharedService } from '../../../services/shared.service';

interface DialogData {
	preset?: ManagementAiPresetResponse;
	availableTypes: ManagementAiPresetRequest.TypeEnum[];
}

const PRESET_PLACEHOLDERS: Record<string, string[]> = {
	CLIP_REPORT_SUMMARY: ['clientName', 'additionalDescription', 'lang'],
	CLIENT_REPORT_SUMMARY: ['clientName', 'additionalDescription', 'lang'],
	CLIP_SIMILAR: ['clips'],
	CLIP_AUTO_TAG: ['tagNames', 'data'],
	CLIP_SENTIMENT: ['clientName', 'additionalDescription', 'clipData', 'tagContext'],
	CLIP_TRANSCRIPTION: ['lang'],
	TRANSCRIPTION_TRANSCRIPTION: ['lang', 'segmentDurationMs'],
	TRANSCRIPTION_PROOFREAD: ['text'],
	CONTRACT_NOTICE_AUTO_TAG: ['tagNames', 'data'],
	CONTRACT_NOTICE_AUTO_TAG_V2: ['tagNames', 'candidates', 'explanations', 'examples', 'data'],
	CLIP_TRANSLATION: ['fromLang', 'toLang', 'text'],
	CONTRACT_NOTICE_TRANSLATION: ['fromLang', 'toLang', 'text'],
	ANALYTICS_SUMMARY: ['clientName', 'lang', 'metricsData'],
};

@Component({
	selector: 'app-ai-preset-dialog',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatDialogModule,
		MatButtonModule,
		MatFormFieldModule,
		MatInputModule,
		MatSelectModule,
		MatIconModule,
		MatDividerModule,
		TranslateModule,
	],
	templateUrl: './ai-preset-dialog.component.html',
	styleUrl: './ai-preset-dialog.component.scss',
})
export class AiPresetDialogComponent {
	form: FormGroup;
	saving = false;
	isEdit = false;
	typeOptions: ManagementAiPresetRequest.TypeEnum[];
	providerOptions = Object.values(ManagementAiPresetRequest.ProviderEnum) as ManagementAiPresetRequest.ProviderEnum[];

	constructor(
		private fb: FormBuilder,
		private dialogRef: MatDialogRef<AiPresetDialogComponent>,
		private apiAiPreset: ManagementAiPresetControllerService,
		private sharedService: SharedService,
		private translate: TranslateService,
		@Inject(MAT_DIALOG_DATA) public data: DialogData
	) {
		this.isEdit = !!data.preset;
		this.typeOptions = data.availableTypes;

		this.form = this.fb.group({
			type: [data.preset?.type ?? null, [Validators.required]],
			provider: [data.preset?.provider ?? null, [Validators.required]],
			model: [data.preset?.model ?? '', [Validators.required]],
			prompt: [data.preset?.prompt ?? '', [Validators.required]],
		});
	}

	get requiredPlaceholders(): string[] {
		const type = this.form?.value?.type as string | null;
		return type ? PRESET_PLACEHOLDERS[type] ?? [] : [];
	}

	isPlaceholderPresent(name: string): boolean {
		const prompt: string = this.form?.value?.prompt ?? '';
		return prompt.includes(`{${name}}`);
	}

	get allPlaceholdersPresent(): boolean {
		return this.requiredPlaceholders.every(name => this.isPlaceholderPresent(name));
	}

	save(): void {
		if (this.form.invalid || this.saving || !this.allPlaceholdersPresent) return;
		this.saving = true;

		const body: ManagementAiPresetRequest = {
			type: this.form.value.type,
			provider: this.form.value.provider,
			model: this.form.value.model.trim(),
			prompt: this.form.value.prompt.trim(),
		};

		const request$ = this.isEdit && this.data.preset?.id
			? this.apiAiPreset.updateAiPreset(body, this.data.preset.id)
			: this.apiAiPreset.createAiPreset(body);

		request$.pipe(take(1)).subscribe({
			next: response => {
				this.sharedService.showNotification.next({
					message: this.translate.instant(this.isEdit ? 'NOTIFICATIONS.RECORD_UPDATED' : 'NOTIFICATIONS.RECORD_CREATED'),
					type: 'SUCCESS',
				});
				this.dialogRef.close(response);
			},
			error: err => {
				this.saving = false;
				this.sharedService.showNotification.next({
					message: `${this.translate.instant('NOTIFICATIONS.RECORD_ACTION_ERROR')} \n ${err?.error?.message ?? ''}`,
					type: 'ERROR',
				});
			},
		});
	}

	cancel(): void {
		this.dialogRef.close();
	}
}
